// src/screens/auth/ForcedPasswordChangeScreen.js
// 76차: 신규 생성 — createStoreAdminAccountCF로 발급된 매장관리자 계정(mustChangePassword:
// true)이 최초 로그인 시 임시 비밀번호를 계속 쓰지 않도록 강제하는 게이트 화면.
// navigation/index.js의 MainTabNavigator가 탭 네비게이터 자리에 이 화면을 렌더하므로
// Stack.Screen으로 push된 게 아니다 — navigation prop 없이 useAuth()만으로 동작.
// 76차: AppHeader 누락 수정 — 헤더가 아예 없어 다른 화면과 톤이 달랐음. 뒤로가기 없이
// (강제 게이트라 이탈 경로는 아래 로그아웃 버튼뿐) 제목만 표시.
// 78차: "비밀번호 변경하고 시작하기" 버튼을 공용 GradientButton(Primary), "로그아웃"을
// TextButton으로 교체
import React, { useState } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, TextInput, TouchableOpacity,
  StyleSheet, KeyboardAvoidingView, Platform,
} from 'react-native';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { changePasswordWithReauth, logout } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/common/AppHeader';
import GradientButton from '../../components/common/GradientButton';
import { TextButton } from '../../components/common/Buttons';
import { MenuPasswordIcon, IconLabel, WarningIcon } from '../../components/common/Icons';
import { COLORS } from '../../constants/theme';

const BG         = COLORS.bg;
const SURFACE    = COLORS.surface;
const BORDER     = 'rgba(212,168,67,0.2)';
const TEXT       = COLORS.text;
const TEXT_SEC   = COLORS.textSec;
const TEXT_MUTED = 'rgba(255,255,255,0.35)';

export default function ForcedPasswordChangeScreen() {
  const { user, refreshProfile } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!currentPw || !newPw || !confirmPw) { showAlert('알림', '모든 항목을 입력해주세요.'); return; }
    if (newPw.length < 8) { showAlert('알림', '새 비밀번호는 8자 이상이어야 합니다.'); return; }
    if (newPw !== confirmPw) { showAlert('알림', '새 비밀번호가 일치하지 않습니다.'); return; }
    try {
      setLoading(true);
      await changePasswordWithReauth({ email: user.email, currentPassword: currentPw, newPassword: newPw });
      await updateDoc(doc(db, 'users', user.uid), { mustChangePassword: false });
      await refreshProfile();
    } catch (e) {
      showAlert('오류', e.code === 'auth/wrong-password'
        ? '임시 비밀번호가 올바르지 않습니다.' : e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: BG }}>
      <AppHeader centerElement={<Text style={s.headerTitle}>비밀번호 변경</Text>} />
      <KeyboardAvoidingView
        style={[s.container, { paddingTop: 24 }]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <IconLabel Icon={MenuPasswordIcon} size={s.title.fontSize || 18} color={s.title.color} textStyle={s.title}>비밀번호 변경이 필요해요</IconLabel>
        <Text style={s.desc}>
          발급받은 임시 비밀번호로 로그인했어요.{'\n'}계속 이용하려면 새 비밀번호로 변경해주세요.
        </Text>

        <Text style={s.label}>임시 비밀번호</Text>
        <TextInput style={s.input} value={currentPw} onChangeText={setCurrentPw}
          secureTextEntry placeholder="발급받은 임시 비밀번호" placeholderTextColor={TEXT_MUTED} autoFocus />
        <Text style={s.label}>새 비밀번호</Text>
        <TextInput style={s.input} value={newPw} onChangeText={setNewPw}
          secureTextEntry placeholder="8자 이상" placeholderTextColor={TEXT_MUTED} />
        <Text style={s.label}>새 비밀번호 확인</Text>
        <TextInput style={s.input} value={confirmPw} onChangeText={setConfirmPw}
          secureTextEntry placeholder="새 비밀번호 재입력" placeholderTextColor={TEXT_MUTED} />
        {newPw && confirmPw && newPw !== confirmPw && (
          <IconLabel Icon={WarningIcon} size={s.hintErr.fontSize || 12} color={s.hintErr.color} textStyle={s.hintErr}>비밀번호가 일치하지 않습니다.</IconLabel>
        )}

        <GradientButton
          label={loading ? '변경 중...' : '비밀번호 변경하고 시작하기'}
          onPress={handleSubmit}
          disabled={loading}
          style={s.saveBtn}
        />

        <TextButton label="로그아웃" onPress={() => logout()} style={s.logoutBtn} textStyle={s.logoutBtnText} />
      </KeyboardAvoidingView>
    </View>
  );
}

const s = StyleSheet.create({
  headerTitle: { color: COLORS.text, fontSize: 15, fontWeight: '800' },
  container: { flex: 1, backgroundColor: BG, paddingHorizontal: 20 },
  title:     { color: TEXT, fontSize: 20, fontWeight: '800', marginBottom: 10 },
  desc:      { color: TEXT_SEC, fontSize: 13, lineHeight: 20, marginBottom: 28 },
  label:     { color: TEXT_SEC, fontSize: 12, fontWeight: '700', marginBottom: 6, marginTop: 14 },
  input: {
    backgroundColor: SURFACE, borderRadius: 10, borderWidth: 1, borderColor: BORDER,
    paddingHorizontal: 14, paddingVertical: 12, color: TEXT, fontSize: 14,
  },
  hintErr:   { color: '#CD6F66', fontSize: 12, marginTop: 8 },
  saveBtn:   { marginTop: 26 },
  logoutBtn:   { alignItems: 'center', marginTop: 18, padding: 8 },
  logoutBtnText: { color: TEXT_MUTED, fontSize: 13, fontWeight: '600' },
});
